import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  getRequiredFields,
  removeEmptyFields,
  setEmptyFieldsToNull,
} from "@/lib/utils";
import { GRADE, MODULE, SUB_MODULE } from "../consts";
import { routes } from "@/routes/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { formValues, schema } from "./validations";
import { useAddSubModuleMutation, useGetModules } from "@/services/masterData";
import { useGetCurriculumGrades } from "@/services/masterData/useGetCurriculumGrades";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import useGetSubmoduleDetailQuery from "@/services/masterData/submodule/useSubmoduleDetailMutation";
import useUpdateSubmodulesMutation from "@/services/masterData/submodule/useUpdateSubmoduleMutation";

const useAddEdit = () => {
  const { action, id } = useParams();
  const navigate = useNavigate();
  const isView = action == routes.view;
  const isEdit = action == routes.edit;
  const isMobile = useIsMobile();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [initialDetailPayload, setInitialDetailPayload] = useState<Record<
    string,
    any
  > | null>(null);

  const form = useForm<formValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      [GRADE]: "",
      [MODULE]: "",
      [SUB_MODULE]: "",
    },
  });

  const {
    data: modulesRes,
    isPending: isModulesLoading,
    error: modulesError,
  } = useGetModules();
  const {
    data: gradesRes,
    isPending: isGradesLoading,
    error: gradesError,
  } = useGetCurriculumGrades();

  const gradeOptions = (gradesRes?.data || []).map((g: any) => ({
    label: g?.curriculum_grade,
    value: String(g?.curriculum_id),
  }));

  const moduleOptions = (modulesRes?.data?.[0]?.records || []).map(
    (m: any) => ({
      label: m?.module_name,
      value: String(m?.module_id),
    })
  );

  const prefill = (location.state as any) || null;
  const { data: detailRes, isPending: isDetailLoading } =
    useGetSubmoduleDetailQuery(
      !prefill && (isEdit || isView) ? String(id) : undefined
    );

  useEffect(() => {
    if ((isEdit || isView) && (prefill || detailRes?.data)) {
      const d = prefill || detailRes.data;
      form.reset({
        [GRADE]: String(d?.curriculum_id ?? d?.curriclum_grade ?? ""),
        [MODULE]: String(d?.module_id ?? ""),
        [SUB_MODULE]: String(d?.sub_module_name ?? ""),
      });
      setInitialDetailPayload({
        name: String(d?.sub_module_name ?? d?.name ?? ""),
        description: d?.description ? String(d?.description) : undefined,
      });
    }
  }, [prefill, detailRes, isEdit, isView, form]);

  const { mutateAsync: updateSubmodule, isPending: isUpdating } =
    useUpdateSubmodulesMutation({
      onSuccess: () => {
        toast.success("Sub Module updated successfully");
        onBackClick();
      },
      onError: (error: any) => {
        toast.error(
          error.response.data?.issues?.message ||
            error.response.data.message ||
            "Failed to update sub module"
        );
      },
    });

  const { mutateAsync: addSubmodule, isPending } = useAddSubModuleMutation({
    onSuccess: () => {
      toast.success("Sub module created successfully");
      onBackClick();
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.issues?.message || error?.response?.data?.message || "Failed to create sub module"
      );
    },
  });

  const requiredFields = getRequiredFields(schema);

  const onBackClick = () => {
    navigate(`/${routes.admin}/${routes.masterData}`);
  };

  const onSubmit = async (data: formValues) => {
    let _data = removeEmptyFields(data);
    _data = setEmptyFieldsToNull(_data);

    let payload = {
      curriculum_id: String(_data[GRADE] ?? ""),
      module_id: String(_data[MODULE] ?? ""),
      sub_module_name: String(_data[SUB_MODULE] ?? ""),
    };

    if (isEdit && id) {
      await updateSubmodule({ id, data: {sub_module_name: String(_data[SUB_MODULE] ?? "")} });
      queryClient.invalidateQueries({ queryKey: ["subModule"] });
      return;
    }
    await addSubmodule({ data: payload });
    queryClient.invalidateQueries({ queryKey: ["subModule"] });
  };

  return {
    form,
    onSubmit,
    requiredFields,
    isView,
    onCancel: onBackClick,
    isMobile,
    action,
    isEdit,
    isLoading: isPending,
    gradeOptions,
    moduleOptions,
    isModulesLoading,
    isGradesLoading,
  };
};

export default useAddEdit;
